import { useFieldArray, useWatch } from "react-hook-form";
import { DatePicker, Input, Label, MultipleSelect, Select } from "../FormComponents";
import useLabels from "../../hooks/useLabels";
import { empty, setOperations } from "../../context/operations/operationsSlice";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  getOperations,
  getUsers,
  newOperation,
  updateOperation,
} from "../../services/utils";
import { ConfirmationModal, CTA, DestinationSelector, OperationRecap, ProtocolConflictModal, TankIcon } from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_REGEX,
} from "../../constants/regex";

// NEW: Import CacheManager for surgical cache updates
import CacheManager from "../../services/cacheManager";
import {
  selectAllLotNames,
  selectConfigs,
  selectOperations,
  selectUser,
  selectUsers,
} from "../../context/selectors";
import { addMinutes, addMonths } from "date-fns";
import { toast } from "react-toastify";
import { emptyLots } from "../../context/lots/lotsSlice";
import { setUsers } from "../../context/users/usersSlice";
import { useEffect, useState } from "react";
import { DestTankDetail } from "../OperationsDetail/Details";
import { NavLink } from "react-router-dom";
import { toastAxios } from "../../services/toastAxios";

const NewLotCreationForm = ({
  operation,
  lots,
  activeWinery,
  tanks,
  handleCloseModal,
  register,
  control,
  errors,
  setError,
  clearErrors,
  getValues,
  setValue,
  lot_id_in,
  isOpen,
  isMobile,
  isDuplicate = false,
}) => {
  const { id } = useParams();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const users = useSelector(selectUsers);
  const operations = useSelector(selectOperations);
  const configs = useSelector(selectConfigs);
  const lotNames = useSelector(selectAllLotNames);
  const [isExactDate, setIsExactDate] = useState(false);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const [conflictProtocols, setConflictProtocols] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [lotTypeMap, setLotTypeMap] = useState({});
  const [tank_id_in, setTankIdIn] = useState({});

  useEffect(() => {
    if (operations?.length === 0) {
      loadOperations();
    }
    if (users?.length === 0) {
      loadUsers();
    }
  }, []);

  useEffect(() => {
    setTankIdIn({
      batch_name: lot_id_in?.name,
      state: lot_id_in?.state?.value,
      color: lot_id_in?.color?.value,
      value: true,    // trick so that the getDestOptions() Select is not disabled
      quantity: Number(lot_id_in?.quantity),
      batch_type: lot_id_in?.batch_type || 0,
    });
  }, [lot_id_in]);

  const { dest_tanks, subtype } = useWatch({ control });

  const {
    fields,
    append,
    trigger,
    prepend,
    remove,
    swap,
    move,
    insert,
    replace,
    update,
  } = useFieldArray({
    control,
    name: "dest_tanks",
  });

  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(loadedOperations));
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  useEffect(() => {
    if (id && (!operations || operations?.length === 0)) loadOperations();
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      if (operation === null) return;

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      setDestTanks(operation);

      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);

      setValue("expire_date", operation?.expire_date);

      setValue("note", operation?.note);
    }
  }, [operations, operation, lots]);

  // useEffect(() => {
  //   const actual_state = dest_tanks?.find((t) => t?.tankData?.state !== null)
  //     ?.tankData?.state;
  //   if (actual_state !== state && actual_state !== undefined) {
  //     replace([{}]);
  //   }
  // }, [state]);

  const setDestTanks = (operation) => {
    const dests = [];

    operation?.dest_tanks?.map((s, i) => {
      const tank = tanks?.find((t) => t.id === s.tank_id);
      const isPlannedByOtherOps = tank?.is_planned && (
        (id === null && tank?.programmed_as_dest?.length > 0) ||
        // (id !== null && !isDuplicate && tank?.programmed_as_dest?.some(p_id => p_id !== Number(id))) ||
        (id !== null && isDuplicate));

      // if same batch_name as src, simulate OLD because it inherits the src name
      const name = tank?.is_planned === true ? tank?.planned_batch_name : tank?.batch_name;
      let is_new = tank?.is_planned && tank?.is_planned_state ? true : s?.is_new;
      if (is_new) {
        is_new = (lots
          ?.find(
            (l) => l?.name === name 
            && l?.status !== "PLANNED" 
            && l?.state === tank?.planned_batch_state 
            && l?.name?.toLowerCase?.()
          ) === undefined);
        
        if (is_new) {
          // NOW detect “duplicate new” earlier in this same op
          const alreadyUsed = operation?.dest_tanks
            .slice(0, i)   // only look at earlier entries
            .some(prev => {
              // whichever field actually holds the “new” name in your data
              const prevName = prev?.is_planned === true 
                ? prev?.planned_batch_name 
                : prev?.batch_name;
              return prev?.is_new && prevName === name;
            });
    
          if (alreadyUsed) {
            is_new = false;
          }
        }
      }
      
      const d = {
        quantity: Number(s?.arrow_quantity),
        tankData: {
          value: tank?.id,
          name: tank?.name,
          label: tank?.name,
          type: tank?.type,
          barrels_num: tank?.barrels_num,
          is_src: false,
          capacity: Number(tank?.capacity),
          is_planned_by_other_ops: isPlannedByOtherOps,              
          planned_quantity: isPlannedByOtherOps ? 
            Number(tank?.planned_quantity) - (!isDuplicate ? Number(s?.arrow_quantity) : 0) : 0,
          is_planned: isPlannedByOtherOps,
          is_planned_state: tank?.is_planned_state,
          planned_subtype: tank?.planned_subtype,
          planned_batch_name: isPlannedByOtherOps ? tank?.planned_batch_name : "",
          planned_batch_type: tank?.planned_batch_type,
          planned_batch_color: tank?.planned_batch_color,
          planned_batch_state: tank?.planned_batch_state,
          quantity: Number(tank?.quantity),
          availability:
            Number(tank?.capacity) -
            Number(tank?.planned_quantity) +
            Number(s?.arrow_quantity),
          color: tank?.color || getValues('color')?.value,
          lot:
              lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
              getLabel("tankState_EMPTY"),
          batch_type: tank?.batch_type === 0 ? 0 : 1,
          batch_name: is_new ? s?.batch_name : lots?.find((lot) => lot?.id === tank?.batch_id)?.name,
          // lot:
          //   lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
          //   getLabel("tankState_EMPTY"),
          programmed_as_src: tank?.programmed_as_src,
          programmed_as_dest: tank?.programmed_as_dest,
        },
        new_batch_name: s?.batch_name || "",
        batch_name_selector: is_new
          ? {
              value: true,
              label: getLabel("lotNewName"),
            }
          : {
              value: false,
              label: s?.batch_name,
            },
      };
      dests.push(d);
    });

    replace(dests);
  };

  const customIdInvalidLotName = "custom-id-invalid-lot-name";

  const customIdNewOp = "custom-id-new-op";
  const customIdUpdateOp = "custom-id-update-op";
  const sendData = async () => {
    if (Object.keys(errors)?.length > 0) return;
    const data = getValues();

    // new lot data
    const total =
      data?.varieties?.length === 1
        ? 100
        : data?.varieties?.reduce((acc, curr) => {
            return acc + (Number(curr?.quantity) || 0);
          }, 0);

    if (total !== 100) {
      data?.varieties?.forEach((v, index) =>
        setError(`varieties.${index}.quantity`, {
          type: "manual",
          message: getLabel("errorWinePercentage100"),
        })
      );
    } else {
      data?.varieties?.forEach((v, index) => clearErrors(`varieties.${index}.quantity`));
    }
    if (errors?.length > 0) handleCloseModal();

    if (!id && lots?.find((l) => l?.name === data?.name?.trim())) {
      toast.error(getLabel("toast_lotNameNotAvailable"), {
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
      });
      handleCloseModal();
    }

    const percentages = data?.varieties?.length > 1 ? data?.varieties?.map(v => Number(v?.quantity)) : [100];
    // console.log('percentages', percentages);
    // return;
    const newLotRequestPayload = {
      // ...data,
      name: data?.name,
      quantity: data?.quantity,
      varieties: data?.varieties?.map((v) => v?.label),
      // years: [data?.years],
      years: Array(percentages?.length).fill(data?.years),
      // origins: [data?.origins],
      origins: Array(percentages?.length).fill(data?.origins),
      percentages: percentages,
      state: data?.state?.value || "GRAPE",
      type: "",
      unit_id: 4,
      label: data?.label?.map((v) => v?.label),
      color: data?.color?.value || "RED",
      quality: data?.quality?.value || "LOW",
      winery_id: activeWinery.id,
      ...(data?.doco && {
        is_doco: true,
        doco_id: data?.doco_id,
        doco_date: addMinutes(
          new Date(data?.doco_date)?.setHours(23, 59, 59),
          -new Date(data?.doco_date).getTimezoneOffset()
        ).toISOString(),
      }),
      // ...(id && { id: id }),
    };
    // console.log('newLotRequestPayload', newLotRequestPayload);
    // return;

    // new tanks organization
    const dests = [];

    const newBatchIds = [];
    const sorted_dest_tanks = data?.dest_tanks?.sort(function (a, b) {
      return b?.batch_name_selector?.value - a?.batch_name_selector?.value;
    })?.filter(
      (d) =>
        d?.tankData?.value &&
        d?.quantity &&
        ((d?.batch_name_selector?.value === true && d?.new_batch_name) ||
          (d?.batch_name_selector?.value === false &&
            d?.batch_name_selector?.label))
    );
    // console.log(sorted_dest_tanks)
    
    sorted_dest_tanks
      ?.forEach((destT, i) => {
        
      const batch_name_value = destT?.batch_name_selector?.value;
      const batch_name_label = destT?.batch_name_selector?.label;
      const new_batch_name = destT?.new_batch_name || batch_name_label;
      
      const bType =
        (batch_name_value &&
        lots?.find((l) => l?.name === batch_name_label) === undefined &&
        !newBatchIds.includes(new_batch_name))
          ? "NEW"
          : (lots?.find(
              (l) =>
                (l?.name === new_batch_name &&
                  batch_name_value === true) ||
                (l?.name === batch_name_label && batch_name_value === false)
            ) ===
              undefined) !==
            undefined
          ? (lots?.find((l) => l?.name === new_batch_name) ===
              undefined &&
              !batch_name_value &&
              destT?.batch_name === undefined) ||
            (batch_name_value === true && // modify an existing operation that created PLANNED batches
              new_batch_name?.length > 0 &&
              !newBatchIds?.includes(new_batch_name) &&
              lots?.find(
                (l) => l?.name === new_batch_name && l?.status === "PLANNED"
              ) !== undefined)
            ? "NEW" // lot not existing yet but created in another operation TO DO check
            : "OLD"
          : null;
    
        const type =
          ((batch_name_value === true &&
          new_batch_name?.length > 0 &&
          !newBatchIds?.includes(new_batch_name) &&
          lots?.find(
            (l) =>
              (l?.name === new_batch_name &&
                batch_name_value === true) ||
              (l?.name === batch_name_label &&
                batch_name_value === false)
          ) === undefined) || 
          (data?.name === batch_name_label && 
            batch_name_value === false && 
            !new_batch_name &&
            !newBatchIds?.includes(batch_name_label)))
            ? "NEW"
            : (lots?.find(
                (l) =>
                  (l?.name === new_batch_name &&
                    batch_name_value === true) ||
                  (l?.name === batch_name_label &&
                    batch_name_value === false)
              ) ===
                undefined) !==
              undefined
            ? new_batch_name && lots?.find((l) => l?.name === new_batch_name) ===
                undefined &&
              !batch_name_value &&
              destT?.batch_name === undefined
              ? "NEW" // lot not existing yet but created in another operation
              : "OLD"
            : null;
        // console.log('lotNames', lotNames) // TO DO va messo il check nel 2o "NEW"

        if (bType === "NEW" && !(new_batch_name in newBatchIds))
          newBatchIds.push(new_batch_name);
        else if (!bType) throw new Error();
        
        // if (type === null) {
        //   toast.error(getLabel("toast_invalidType"), {
        //     toastId: customIdInvalidLotName,
        //     position: toast.POSITION.BOTTOM_RIGHT,
        //     exclude: true,
        //     autoClose: 5000,
        //   });
        //   return;
        // }

        const t = tanks?.find((t) => t.id === destT?.tankData?.value) || {};
        
        const dest = {
          tank_id: destT?.tankData?.value,
          quantity: Number(destT.quantity),
          new_batch_name: (batch_name_value === true
            ? new_batch_name
            : batch_name_label
          )?.trim(),
          new_batch_type: bType,
          old_batch_id:
            t?.batch_id || "",
          old_quantity:
            t?.quantity || 0,
          new_quantity:
            (Number(
              t?.quantity || 0
            ) || 0) + (Number(destT.quantity) || 0),
        };
        dests.push(dest);
      });

    if (dests?.length === 0) {
      toast.error(getLabel("toast_missingDestTanks"), {
        toastId: customIdInvalidLotName,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: 5000,
      });
      return;
    }

    const tmpDate = new Date(data?.expire_date)?.setHours(23, 59, 59);

    const requestPayload = {
      dest_tanks: dests,
      priority: data?.priority?.value,
      state: data?.state?.value,
      is_exact_date: isExactDate,
      // expire_date: addMinutes(
      //   new Date(data?.expire_date)?.setHours(23, 59, 59),
      //   -new Date(data?.expire_date).getTimezoneOffset()
      // ).toISOString(),
      expire_date: new Date(tmpDate)?.toISOString(),
      type: "NEW_LOT",
      winery_id: activeWinery?.id,
      cellarman_ids: data?.cellarman_ids?.map((cellMan) => cellMan?.value),
      note: data?.note,
      src_tanks: [newLotRequestPayload],
    };
    console.log("requestPayload", requestPayload);
    // return;

    let response = null;
    if (id && !isDuplicate) {
      response = await toastAxios(
        () => updateOperation(id, requestPayload, axiosPrivate),
        customIdUpdateOp,
        { name: operation?.type || "" },
        getLabel,
      );
    } else {
      response = await toastAxios(
        () => newOperation(requestPayload, axiosPrivate),
        customIdNewOp,
        { name: operation?.type || "" },
        getLabel,
      );
      if (response?.requires_confirmation === true) {
        setPendingPayload(requestPayload);
        setConflictProtocols(response.affected_protocols || []);
        setIsConflictOpen(true);
        return;
      }
    }

    if (response && response?.success) {
      // NEW: Pass affected_entities to updateStore for surgical cache updates
      updateStore(response?.affected_entities);
      navClickHandler();
    }
  };

  const handleConflictConfirm = async () => {
    setIsConflictOpen(false);
    const response = await toastAxios(
      () => newOperation({ ...pendingPayload, confirm_protocol_impact: true }, axiosPrivate),
      customIdNewOp,
      { name: operation?.type || "" },
      getLabel,
    );
    if (response?.success) {
      updateStore(response.affected_entities);
      navClickHandler();
    }
  };

  // NEW: Updated to accept affected_entities for surgical cache updates
  const updateStore = async (affectedEntities) => {
    // if (id && !isDuplicate) {
    //   dispatch(updateOp(payloadOp));
    // } else {
    //   dispatch(add(payloadOp));
    // }

    // NEW: Surgical cache update using affected_entities
    if (affectedEntities) {
      const cacheManager = new CacheManager(dispatch, axiosPrivate);
      await cacheManager.processAffectedEntities(affectedEntities);
    }
    // ⚠️ FALLBACK: If backend doesn't return affected_entities yet
    else {
      dispatch(empty());
      dispatch(emptyLots());
    }
  };

  const navClickHandler = () => {
    if (id) {
      navigate(`/programming/new_lot/${id}`);
    } else {
      navigate("/programming");
    }
  };

  return (
    <div className="operation_form_wrapper">
      <div className="operation_form_wrapper_row vertical">
        <DestinationSelector
          id={id}
          type={"NEW_LOT"}
          subtype={subtype?.value}
          operation={operation}
          register={register}
          control={control}
          errors={errors}
          clearErrors={clearErrors}
          setError={setError}
          trigger={trigger}
          setValue={setValue}
          getValues={getValues}
          tanks={tanks}
          lots={lots}
          lotNames={lotNames}
          tank_id_in={tank_id_in}
          fields={fields}
          // dest_tanks={dest_tanks}
          append={append}
          update={update}
          remove={remove}
          // getTankData={getTankData}
          lotTypeMap={lotTypeMap}
          setLotTypeMap={setLotTypeMap}
          isMobile={isMobile}
          watchedArray={dest_tanks}
          forceLiters={true}
        />
      </div>
      <div className="operation_form_wrapper_row">
        <MultipleSelect
          name={`cellarman_ids`}
          control={control}
          label={getLabel("operationUser")}
          placeholder={getLabel("operationUserPlaceholder")}
          error={errors["cellarman_ids"]}
          required={getLabel("inputRequiredError")}
          options={(user.role === 2
            ? users?.filter(
                (u) => u.username != user.userName && u.role_id === 3
              )
            : users
          )?.map?.((user) => {
            return {
              value: user?.id,
              label: user?.username,
            };
          })}
          tooltip={getLabel("destemmingCellarmenTooltip")}
        />
        <Select
          name="priority"
          control={control}
          label={getLabel("operationPriority")}
          placeholder={getLabel("operationPriorityPlaceholder")}
          error={errors}
          required={getLabel("inputRequiredError")}
          options={[
            {
              label: getLabel("lotQuality_HIGH"),
              value: "2",
            },
            {
              label: getLabel("lotQuality_MEDIUM"),
              value: "1",
            },
            {
              label: getLabel("lotQuality_LOW"),
              value: "0",
            },
          ]}
          // defaultValue={{
          //     value: "",
          //     label: "",
          //   }}
        />
        <DatePicker
          name="expire_date"
          control={control}
          label={getLabel("operationExpireDateForm")}
          error={errors["expire_date"]}
          required={getLabel("inputRequiredError")}
          dateFormat={configs.shortDateFormat}
          placeholder={getLabel("expiringDatePlaceholder")}
        />
        {getValues("expire_date") && <Input
          name={"expire_date_exact"}
          register={register}
          label={getLabel("isExactDate")}
          error={errors["expire_date_exact"]}
          // isLabel={false}
          type={"checkbox"}
          checked={isExactDate}
          onChange={() => setIsExactDate((prev) => !prev)}
          customClassName={"confirm no_margin_top"}
          tooltip={getLabel("destemmingExactDate")}
        />}
      </div>
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={sendData}
        onClose={handleCloseModal}
        description={getLabel("modalNewLotDescription", {
          name: operation?.id || "",
        })}
        isOperationRecap={true}
        totalStep={0}
        errors={errors}
        getValues={getValues}
      >
        <OperationRecap
          type={"NEW_LOT"}
          control={control}
          register={register}
          getValues={getValues}
          errors={errors}
          lots={lots}
          lotNames={lots?.map((l) => l?.name)}
          dest_tanks={dest_tanks?.map((d, index) => { return {
            ...d?.tankData, 
            name: d?.tankData?.label,
            color: d?.tankData?.color || getValues("color")?.value,
            add_quantity: Number(d?.quantity),
            old_quantity: Number(d?.quantity) + Number(d?.tankData?.quantity),
            quantity: Number(d?.quantity) + Number(d?.tankData?.quantity),
            batch_name: d?.batch_name_selector?.value === true
            ? d?.new_batch_name
            : d?.batch_name_selector?.label,
          }})}
          src_tanks={[
            {
              name: getValues("name"),
              quantity: getValues("quantity"),
              initial_quantity: getValues("quantity"),
              capacity: getValues("quantity"),
              color: getValues("color")?.value,
              state: getValues("state")?.value,
            },
          ]}
        />
      </ConfirmationModal>
      <ProtocolConflictModal
        isOpen={isConflictOpen}
        protocols={conflictProtocols}
        onConfirm={handleConflictConfirm}
        onCancel={() => setIsConflictOpen(false)}
      />
    </div>
  );
};

export default NewLotCreationForm;
